'use strict';

/**
 * Task Catalog — the human-facing "what can this subject do?" layer for the Entitlements screen.
 *
 * The RBAC system underneath is fine-grained: capabilities are `<Entity>-<Action>` hashes (Permission-Catalog.js),
 * granted to a Role / Permission Group as RolePermission rows and composed via RoleEdge. Admins shouldn't have to
 * think in those terms. This catalog maps each capability bundle to ONE human **task** ("Manage users", "Import
 * reference data"). Turning a task on for a subject grants its bundle (via a lazily-created Permission Group + a
 * RoleEdge — one group per (customer, task), reused across roles); off removes the RoleEdge.
 *
 * Pure data + helpers — no pict / DOM / network — so it unit-tests in isolation (mirrors Permission-Catalog.js).
 *
 * === DRAFT: the hashes below are a first cut. Verify each against access/Permission-Catalog.json before trusting
 * a task, and tune the bundles with domain knowledge — this file is meant to be curated. ===
 *
 * Task shape:
 *   Key         : stable slug (also the Permission Group name suffix, Task-<Key>)
 *   Label       : the toggle's human label
 *   Area        : grouping header on the screen
 *   Description : one line explaining what it lets someone do
 *   Broad       : true → a coarse, powerful grant; the screen flags it and asks for extra confirmation
 *   RequiresPermissionLevel : e.g. 'CustomerAdmin' / 'RoleManager' — set the subject role's tier (coarse editors
 *                 that gate on PermissionLevel rather than a capability). Omit for pure capability tasks.
 *   Capabilities: { <MembershipScope>: [ '<Entity>-<Action>', … ] } — the hashes granted. Most live at
 *                 'CustomerMembership'; add 'ProjectMembership' / 'ContractMembership' where a task is scoped deeper.
 */

// The master lists an administrator curates (Headlight-MasterLists.js) are the source of truth for the per-list
// entitlement tasks — we generate one task per list (below) so entitlements track the registry rather than drifting
// from a hand-kept copy.
const libMasterLists = require('../master-lists/Headlight-MasterLists.js');

const CURATED_TASKS =
[
	// ── People & Access ────────────────────────────────────────────────────────
	{
		Key: 'manage-users', Label: 'Manage users', Area: 'People & Access',
		Description: 'Create and edit user accounts (accounts are deactivated, not deleted). Assigning roles to users lives under "Manage roles & permissions".',
		Capabilities: { CustomerMembership: [ 'User-Read', 'User-Create', 'User-Update' ] },
	},
	{
		Key: 'manage-teams', Label: 'Manage teams and document grants', Area: 'People & Access',
		Description: 'Create teams, enroll people in them with a role, grant a team (or a role) documents, and record which labs a team staffs, from Settings → Teams. Members of a granted team can open and edit those documents, and reach their projects, without being added to the project. A team linked to a lab is granted every test form for that lab\'s tests; link it to each lab it works, since a lab\'s child labs are not included.',
		Capabilities: { CustomerMembership: [ 'Team-Read', 'Team-Create', 'Team-Update', 'Team-Delete', 'TeamUserJoin-Read', 'TeamUserJoin-Create', 'TeamUserJoin-Update', 'TeamUserJoin-Delete', 'DocumentAccessJoin-Read', 'DocumentAccessJoin-Create', 'DocumentAccessJoin-Update', 'DocumentAccessJoin-Delete', 'LabTeamJoin-Read', 'LabTeamJoin-Create', 'LabTeamJoin-Update', 'LabTeamJoin-Delete' ] },
	},
	{
		// UX gate for the #/FormAccess screen (EntityOwnershipAssignment/Document). Saving still needs CustomerAdmin on
		// the config service.
		Key: 'configure-form-access', Label: 'Configure who can open new forms', Area: 'People & Access',
		Description: 'Choose, for each type of form, the roles that can open and edit a new one as soon as it is created, on any project, without being a project member. Affects forms created afterwards; existing forms keep their access.',
		Capabilities: { CustomerMembership: [ 'FormAccess-Configure' ] },
	},
	{
		Key: 'manage-roles', Label: 'Manage roles & permissions', Area: 'People & Access', Broad: true,
		Description: 'Use the Access workspace to build roles and permission groups and grant capabilities. This is how access itself is administered — grant sparingly.',
		RequiresPermissionLevel: 'RoleManager',
		Capabilities: { CustomerMembership: [ 'Role-Read', 'Role-Create', 'Role-Update', 'Role-Delete', 'Permission-Grant' ] },
	},
	{
		Key: 'manage-customer-team', Label: 'Manage Customer Team', Area: 'People & Access', Broad: true,
		Description: 'Platform super-users only: mark internal employees as Headlight platform admins (who may impersonate a customer’s configuration admin) from the Manage Customers screen. Grant sparingly.',
		Capabilities: { CustomerMembership: [ 'CustomerTeam-Read', 'CustomerTeam-Update' ] },
	},
	{
		Key: 'impersonate-customers', Label: 'Impersonate customer accounts', Area: 'People & Access', Broad: true,
		Description: 'Sign in as a customer’s configuration-admin account from the Manage Customers screen to administer that customer’s configuration. For internal HeadLight support staff (customer 1, level 2+). Grant only to trusted staff.',
		Capabilities: { CustomerMembership: [ 'Customer-Impersonate' ] },
	},
	{
		Key: 'support-view', Label: 'View support cases (read-only)', Area: 'People & Access',
		Description: 'See your organization’s open support cases and the updates from the support team in the read-only portal (#/SupportPortal). No filing or triage. For customer stakeholders who just need visibility.',
		Capabilities: { CustomerMembership: [ 'Support-View' ] },
	},
	{
		Key: 'support-agent', Label: 'Self-serve support (tier-1)', Area: 'People & Access',
		Description: 'File and track your own support calls in the Support console (#/Support). Tier-1 self-service — the categories a caller may file under are set per customer. For customer end-users.',
		Capabilities: { CustomerMembership: [ 'Support-Agent' ] },
	},
	{
		Key: 'support-manage', Label: 'Manage the Support console', Area: 'People & Access',
		Description: 'Full support management: triage every call, run the approval workflow, all request categories, and see the support metrics. For HeadLight support staff and customer support leads.',
		Capabilities: { CustomerMembership: [ 'Support-Agent', 'Support-Manage' ] },
	},
	{
		Key: 'manage-certification-types', Label: 'Manage certification types & setup', Area: 'People & Access',
		Description: 'Create and edit certification types and configure what each one certifies (users, labs, materials, equipment, tests) and certifies-for — the setup behind the Certifications screen. (Saving customer-level configuration may also require a customer administrator.)',
		Capabilities: { CustomerMembership: [ 'CertificationType-Read', 'CertificationType-Create', 'CertificationType-Update', 'CertificationType-Delete' ] },
	},

	// ── Projects & Contracts ────────────────────────────────────────────────────
	{
		Key: 'manage-projects', Label: 'Manage projects', Area: 'Projects & Contracts',
		Description: 'Create and edit projects and their settings.',
		Capabilities: { CustomerMembership: [ 'Project-Read', 'Project-Create', 'Project-Update' ] },
	},
	{
		Key: 'bulk-delete-project-records', Label: 'Bulk delete project records', Area: 'Projects & Contracts', Broad: true,
		Description: 'Unlock the bulk-delete tools on a project — select and remove many of its line items, bid items, equipment links, personnel links, and labels at once. Line/bid item deletes are recoverable soft-deletes; equipment/personnel/labels are unlinked from the project (the shared records stay). A powerful batch action; grant sparingly.',
		Capabilities: { CustomerMembership: [ 'Project-BulkDelete', 'LineItem-Delete', 'BidItem-Delete' ] },
	},
	{
		Key: 'manage-project-staffing', Label: 'Manage project staffing', Area: 'Projects & Contracts',
		Description: 'Add and remove the people on a project from the project\'s Staffing tab, and set their project role (PE, Assistant PE, Contractor Approver, ADA, HQ AE, …).',
		Capabilities: { CustomerMembership: [ 'User-Read', 'Role-Read', 'User-Project-Associate', 'User-Project-Disassociate', 'User-Role-Associate', 'User-Role-Disassociate' ] },
	},
	{
		Key: 'manage-contracts', Label: 'Manage contracts', Area: 'Projects & Contracts',
		Description: 'Create and edit contracts. (Bid items and line items are their own entries under Master Lists.)',
		Capabilities: { CustomerMembership: [ 'Contract-Read', 'Contract-Create', 'Contract-Update' ] },
	},
	{
		Key: 'manage-project-contract-types', Label: 'Manage project & contract types', Area: 'Projects & Contracts',
		Description: 'Create and edit Project Types and Contract Types — their status lifecycles, default dates, allowed project types, default funding, and the prototype each new project/contract is cloned from. Unlocks the Project Types and Contract Types screens.',
		Capabilities: { CustomerMembership: [ 'ProjectType-Read', 'ProjectType-Create', 'ProjectType-Update', 'ProjectType-Delete', 'ContractType-Read', 'ContractType-Create', 'ContractType-Update', 'ContractType-Delete' ] },
	},
	{
		Key: 'manage-organizations', Label: 'Manage organizations', Area: 'Projects & Contracts',
		Description: 'Maintain the organizations (contractors, agencies) directory.',
		Capabilities: { CustomerMembership: [ 'Organization-Read', 'Organization-Create', 'Organization-Update' ] },
	},
	{
		Key: 'manage-important-files', Label: 'Manage important project files', Area: 'Projects & Contracts',
		Description: 'Organize a project’s important files: create and rename folders, move files and folders between them, and add or remove files. Anyone who can see a project can already browse and download its important files read-only — this unlocks changing them.',
		Capabilities: { CustomerMembership: [ 'ProjectFile-Read', 'ProjectFile-Create', 'ProjectFile-Update', 'ProjectFile-Delete', 'ProjectFileFolder-Read', 'ProjectFileFolder-Create', 'ProjectFileFolder-Update', 'ProjectFileFolder-Delete' ] },
	},

	// ── Field Data ──────────────────────────────────────────────────────────────
	{
		// Enabled 2026-10-04. An "observation type" = one ObservationManifest (Name/DisplayName/Category/IDCustomer)
		// + its ObservationRequirementSpecification rows (each → a shared ArtifactType). Every one of those writes is
		// gated by the per-entity `<Entity>-<Action>` capability — HeadlightController.allowsCreate/Update/Delete →
		// hasPermissionInContext('<Entity>-Create' …) — so granting this task is what actually lets a customer admin
		// manage their OWN types through the #/ObservationType editor (nothing to change server-side to "open writes":
		// the schema Authorization block's Deny/MyCustomer is VESTIGIAL in meadow-endpoints 4.x and is not the gate).
		// Customer scoping is enforced by the server, not this grant: ObservationManifest + ArtifactType carry
		// IDCustomer, so HeadlightController.checkAndInferRecordIDCustomer stamps the caller's customer onto every
		// create and rejects cross-customer writes; ObservationRequirementSpecification has NO IDCustomer, so
		// ObservationManifestBundle.enforceRequirementSpecCustomerScope scopes its writes to the parent manifest's
		// customer. Net: a customer can only create/edit their own types — never the global (IDCustomer 0) platform
		// defaults (they clone a default to customize it). We include the full ObservationRequirementSpecification
		// CRUD set because the editor creates/updates AND deletes requirement rows when a type is edited.
		Key: 'manage-observation-types', Label: 'Manage observation types', Area: 'Field Data',
		Description: 'Build and edit your organization’s observation types — their requirements and artifact types. New and edited types are automatically scoped to your organization; the shared platform defaults stay read-only (clone one to customize it).',
		Capabilities: { CustomerMembership: [ 'ObservationManifest-Read', 'ObservationManifest-Create', 'ObservationManifest-Update', 'ObservationRequirementSpecification-Read', 'ObservationRequirementSpecification-Create', 'ObservationRequirementSpecification-Update', 'ObservationRequirementSpecification-Delete', 'ArtifactType-Read', 'ArtifactType-Create', 'ArtifactType-Update' ] },
	},
	{
		Key: 'manage-observation-tags', Label: 'Manage observation tags', Area: 'Field Data',
		Description: 'Maintain the tags used to categorize observations.',
		Capabilities: { CustomerMembership: [ 'ObservationTag-Read', 'ObservationTag-Create', 'ObservationTag-Update', 'ObservationTag-Delete' ] },
	},
	{
		Key: 'manage-document-types', Label: 'Manage document types', Area: 'Field Data',
		Description: 'Maintain report/document definitions.',
		Capabilities: { CustomerMembership: [ 'ReportNamedInstance-Read', 'ReportNamedInstance-Create', 'ReportNamedInstance-Update' ] },
	},
	{
		// UX-only gate (not server-enforced): unlocks the raw-JSON metadata editor for report named instances in the
		// Forms Editor. Sits beneath "Manage document types" — a document-type editor manages RNIs through the guided
		// form; this additionally lets a power user hand-edit the raw Options JSON (bypassing the guided fields).
		Key: 'edit-report-json', Label: 'Edit report JSON (advanced)', Area: 'Field Data',
		Description: 'Allow hand-editing the raw Options JSON of a report named instance in the Forms Editor. Without it the JSON view is read-only. This is a UX gate — it controls the editor affordance, not server-side write permission.',
		Capabilities: { CustomerMembership: [ 'ReportNamedInstance-EditJSON' ] },
	},
	{
		// UX-only gate (not server-enforced): unlocks the raw-JSON "Advanced" view of a record's per-customer custom
		// metadata (its CustomProperties blob) in the record workspaces. Without it only the guided metadata form is
		// shown; with it a power user can toggle to see/edit the raw JSON directly (bypassing the guided fields). See
		// the EntityMetadataForm provider — reusable across entity types (Project first).
		Key: 'edit-custom-metadata-json', Label: 'Edit custom metadata JSON (advanced)', Area: 'Projects & Contracts',
		Description: 'Allow toggling a record’s Custom Properties to the raw-JSON editor (bypassing the guided metadata form). Without it the guided form is shown and the raw JSON is hidden. This is a UX gate — it controls the editor affordance, not server-side write permission.',
		Capabilities: { CustomerMembership: [ 'CustomMetadata-Advanced' ] },
	},
	{
		// UX gate for the bespoke #/ATMDashboardConfig screen. Unlocks editing the Audit of Testing & Materials /
		// Sample Plan Checklist dashboard configuration — the validation rules (MaterialsAudit namespace) and the
		// display columns (AppDashboards SamplePlan). The screen writes cascading config at Customer/Project scope;
		// the Configuration/Put server gate stays authoritative.
		Key: 'configure-atm-dashboard', Label: 'Configure the Audit of Testing and Materials dashboard', Area: 'Field Data',
		Description: 'Configure the Audit of Testing & Materials (ATM) / Sample Plan Checklist dashboard — the validation rules (which conformance checks run, the pass/fail vocabulary, accepted-quantity basis) and which columns the checklist shows. Sets customer-wide defaults with optional per-project overrides.',
		Capabilities: { CustomerMembership: [ 'MaterialsAudit-Configure' ] },
	},

	// ── Sample Configuration ─────────────────────────────────────────────────────
	{
		// UX gate for the bespoke #/SampleListColumnsConfig screen. Unlocks choosing which columns the config
		// app's Samples list (#/PSRS/Sample/List) shows + their header labels, saved as a cascading
		// MasterListCatalog Configuration (Platform / Customer / My Settings). The Configuration/Put server gate
		// stays authoritative.
		Key: 'configure-sample-list-columns', Label: 'Configure the Samples list columns', Area: 'Sample Configuration',
		Description: 'Choose which columns the Samples list shows and rename their headers. Save as a platform default, a customer-wide default, or a personal preference.',
		Capabilities: { CustomerMembership: [ 'SampleListColumns-Configure' ] },
	},
	{
		// UX gate for the bespoke #/SampleQRCodeConfig screen. Unlocks configuring how new sample QR codes are
		// generated (standard vs SiteManager/legacy format), stored on the customer's CustomProperties.
		Key: 'configure-sample-qr-code', Label: 'Configure sample QR codes, labels and scanning', Area: 'Sample Configuration',
		Description: 'Configure how new sample QR codes are generated and what the printed label shows, whether Fieldbook on iPad shows its QR scanner, and the status a sample takes when it is scanned into a location (Sample QR Codes and Sample Scanning screens), for your customer.',
		Capabilities: { CustomerMembership: [ 'SampleQRCode-Configure' ] },
	},
	{
		// UX gate for the #/EntityDestinations screen: where scanned labels and emailed links open, per role.
		Key: 'configure-entity-destinations', Label: 'Configure where records open', Area: 'Sample Configuration',
		Description: 'Choose where a scanned QR label or an emailed link takes each person, by role and record state, for your customer.',
		Capabilities: { CustomerMembership: [ 'EntityDestinations-Configure' ] },
	},
	{
		// UX gate for the #/DashboardAccess screen. Saving still needs CustomerAdmin on the config service.
		Key: 'configure-dashboard-access', Label: 'Configure dashboard access', Area: 'Sample Configuration',
		Description: 'Choose which dashboards each role sees, the one they open first, and what data each dashboard shows them, and which classic dashboards Fieldbook offers on a project (Dashboard Access and Fieldbook Dashboards screens), for your customer.',
		Capabilities: { CustomerMembership: [ 'DashboardAccess-Configure' ] },
	},
	{
		// Read by the Sample workspace (samples/SampleWorkspace-Provider.js canOverrideLock). Browser-side only: the API
		// doesn't refuse changes to a locked sample, so this is a convenience, not a control.
		Key: 'override-sample-lock', Label: 'Change locked samples', Area: 'Sample Configuration', Broad: true,
		Description: 'Edit, delete and change the line items and tests of a sample that is locked. Without this, a locked sample is read-only apart from its documents and notes.',
		Capabilities: { CustomerMembership: [ 'SampleLock-Override' ] },
	},

	// ── Communications ──────────────────────────────────────────────────────────
	{
		Key: 'manage-email-notifications', Label: 'Manage email notifications', Area: 'Communications',
		Description: 'Configure the email notification rules.',
		Capabilities: { CustomerMembership: [ 'EmailRule-Read', 'EmailRule-Create', 'EmailRule-Update', 'EmailRule-Delete' ] },
	},

	// ── Documentation ─────────────────────────────────────────────────────────────
	{
		Key: 'edit-documentation', Label: 'Edit in-app help documentation', Area: 'Documentation',
		Description: 'Edit the in-app help pages in place (the ✎ Edit button in the documentation panel). Edits are saved as THIS customer’s override of the platform default; platform super-users editing edit the default that every customer inherits.',
		Capabilities: { CustomerMembership: [ 'AcademyContent-Read', 'AcademyContent-Create', 'AcademyContent-Update', 'AcademyContent-Delete' ] },
	},

	// ── Contract Administration ─────────────────────────────────────────────────────
	// The financial arc (funds, funding sources, categories, accounts) — its own Entitlements tab. The per-entity
	// management tasks below are the re-homed Pay & Funding master lists (see MASTER_LIST_TASKS); these two carry
	// the grants NOT covered by list CRUD: the funding-wiring association verbs, and module access.
	{
		Key: 'associate-funding', Label: 'Wire funding to contracts, projects & line items', Area: 'Contract Administration',
		Description: 'Link funding sources, funds, and categories to contracts, projects, and line items — the associations the project-funding screen creates. Does not include editing the funds/sources themselves (those are the management tasks in this tab).',
		Capabilities: { CustomerMembership: [
			'FundingSource-Contract-Associate', 'FundingSource-Contract-Disassociate',
			'FundingSource-Fund-Associate', 'FundingSource-Fund-Disassociate',
			'FundingSource-FundingCategory-Associate', 'FundingSource-FundingCategory-Disassociate',
			'FundingSource-Project-Associate', 'FundingSource-Project-Disassociate',
			'FundingSource-LineItem-Associate', 'FundingSource-LineItem-Disassociate',
			'Fund-Project-Associate', 'Fund-Project-Disassociate',
			'Fund-Document-Associate', 'Fund-Document-Disassociate',
			'FundingCategory-Project-Associate', 'FundingCategory-Project-Disassociate',
			'FundingCategory-LineItem-Associate', 'FundingCategory-LineItem-Disassociate' ] },
	},
	{
		Key: 'access-contract-admin', Label: 'Access the Contract Administration module', Area: 'Contract Administration',
		Description: 'Open the Contract Administration surface (payments, change orders, funding) — the module gate that lets the funding screens load.',
		Capabilities: { CustomerMembership: [ 'Module-ContractAdmin-Access' ] },
	},

];

// ── Master Lists — one explicit task per curated list, generated from the registry ────────────────────────────
// Each grants Read/Create/Update on the list's entity. `Domain` (from the registry) is kept for optional
// sub-grouping on the screen. New/renamed lists flow through automatically; the screen shows them individually.
const MASTER_LIST_TASKS = (libMasterLists || []).map((pList) =>
({
	Key: `manage-list-${pList.Entity}`,
	Label: `Manage ${pList.Label || pList.Entity}`,
	// Pay & Funding lists live under the dedicated Contract Administration tab; everything else stays in Master Lists.
	Area: (pList.Domain === 'Pay & Funding') ? 'Contract Administration' : 'Master Lists',
	Domain: pList.Domain || '',
	MasterList: true,
	Description: `Add and edit entries in the ${pList.Label || pList.Entity} list.`,
	Capabilities: { CustomerMembership: [ `${pList.Entity}-Read`, `${pList.Entity}-Create`, `${pList.Entity}-Update` ] },
}));

// ── Administration — coarse tasks last (they set a PermissionLevel tier, not a capability bundle) ──────────────
const ADMIN_TASKS =
[
	{
		Key: 'core-platform-admin', Label: 'Core Platform Administration', Area: 'Administration', Broad: true,
		Description: 'Broad administrative editing for the settings that aren’t individually entitled — approval workflows, cascading configuration, in-app help, the data lake, and reference-list setup. A powerful, all-in role; grant only to trusted administrators.',
		RequiresPermissionLevel: 'CustomerAdmin',
		Capabilities: {},
	},
];

const TASKS = CURATED_TASKS.concat(MASTER_LIST_TASKS, ADMIN_TASKS);

/** All tasks, in catalog order. */
function getTasks() { return TASKS; }

/** A task by Key, or null. @param {string} pKey */
function getTask(pKey) { return TASKS.find((pTask) => pTask.Key === pKey) || null; }

/** Tasks grouped by Area, in catalog order: [ { Area, Tasks:[…] }, … ]. */
function tasksByArea()
{
	const tmpOrder = [];
	const tmpByArea = {};
	TASKS.forEach((pTask) =>
	{
		if (!tmpByArea[pTask.Area]) { tmpByArea[pTask.Area] = { Area: pTask.Area, Tasks: [] }; tmpOrder.push(tmpByArea[pTask.Area]); }
		tmpByArea[pTask.Area].Tasks.push(pTask);
	});
	return tmpOrder;
}

/** Flatten a task's capability hashes across every membership scope → [ { Scope, Hash }, … ]. @param {object} pTask */
function taskGrants(pTask)
{
	const tmpGrants = [];
	const tmpCaps = (pTask && pTask.Capabilities) || {};
	Object.keys(tmpCaps).forEach((pScope) => (tmpCaps[pScope] || []).forEach((pHash) => tmpGrants.push({ Scope: pScope, Hash: pHash })));
	return tmpGrants;
}

module.exports = { TASKS, getTasks, getTask, tasksByArea, taskGrants };
